/**
 * @fileoverview OMS source API service with TanStack Query hooks.
 * @module features/settings/api/omsSourceService
 */

import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import {
  OmsSourceArraySchema,
  MapOmsSourceResponseSchema,
  UnmapOmsSourceResponseSchema,
  BaseLinkerOrderSourceArraySchema,
  SyncOmsSourcesResponseSchema,
  type OmsSource,
  type MapOmsSourceResponse,
  type UnmapOmsSourceResponse,
  type BaseLinkerOrderSource,
  type SyncOmsSourcesResponse,
  type OmsType,
} from '../schemas/omsSourceSchema'
import { STORES_QUERY_KEY } from './storeService'

// ============ Query Keys ============

export const OMS_SOURCES_QUERY_KEY = ['oms-sources'] as const
export const OMS_SOURCES_FILTERED_KEY = (
  filter: string | undefined
): readonly ['oms-sources'] | readonly ['oms-sources', string] =>
  filter ? (['oms-sources', filter] as const) : OMS_SOURCES_QUERY_KEY
export const BASELINKER_SOURCES_QUERY_KEY = ['baselinker', 'order-sources'] as const

// ============ API Functions ============

/**
 * Fetches all OMS sources for the organization.
 * @param filter - Optional filter: 'mapped' | 'unmapped'
 */
export async function getOmsSources(filter?: 'mapped' | 'unmapped'): Promise<OmsSource[]> {
  const url = filter ? `/oms-sources?filter=${filter}` : '/oms-sources'
  const res = await apiClient.get(url)
  return OmsSourceArraySchema.parse(res.data)
}

/**
 * Maps an OMS source to a store.
 */
export async function mapOmsSource(id: number, storeId: number): Promise<MapOmsSourceResponse> {
  const res = await apiClient.post(`/oms-sources/${id}/map`, { storeId })
  return MapOmsSourceResponseSchema.parse(res.data)
}

/**
 * Unmaps an OMS source from its store.
 */
export async function unmapOmsSource(id: number): Promise<UnmapOmsSourceResponse> {
  const res = await apiClient.delete(`/oms-sources/${id}/map`)
  return UnmapOmsSourceResponseSchema.parse(res.data)
}

/**
 * Fetches available BaseLinker order sources for mapping.
 */
export async function getBaseLinkerOrderSources(): Promise<BaseLinkerOrderSource[]> {
  const res = await apiClient.get('/baselinker/order-sources')
  return BaseLinkerOrderSourceArraySchema.parse(res.data)
}

/**
 * Syncs OMS sources from an external OMS (e.g., BaseLinker).
 * @param omsType - The OMS type to sync
 * @param force - If true, bypass direct store conflict check
 */
export async function syncOmsSources(
  omsType: OmsType,
  force = false
): Promise<SyncOmsSourcesResponse> {
  const res = await apiClient.post(`/oms-sources/sync?omsType=${omsType}&force=${force}`)
  return SyncOmsSourcesResponseSchema.parse(res.data)
}

// ============ React Query Hooks ============

/**
 * Hook to fetch all OMS sources.
 * @param filter - Optional filter: 'mapped' | 'unmapped'
 */
export function useOmsSourcesQuery(
  filter?: 'mapped' | 'unmapped'
): UseQueryResult<OmsSource[], Error> {
  return useQuery({
    queryKey: OMS_SOURCES_FILTERED_KEY(filter),
    queryFn: () => getOmsSources(filter),
    staleTime: 30_000, // 30 seconds
  })
}

/**
 * Hook to fetch available BaseLinker order sources.
 */
export function useBaseLinkerOrderSourcesQuery(
  enabled = true
): UseQueryResult<BaseLinkerOrderSource[], Error> {
  return useQuery({
    queryKey: BASELINKER_SOURCES_QUERY_KEY,
    queryFn: getBaseLinkerOrderSources,
    staleTime: 60_000, // 1 minute
    enabled,
  })
}

/**
 * Hook to map an OMS source to a store.
 */
export function useMapOmsSourceMutation(): UseMutationResult<
  MapOmsSourceResponse,
  Error,
  { id: number; storeId: number }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, storeId }) => mapOmsSource(id, storeId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: OMS_SOURCES_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: STORES_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: BASELINKER_SOURCES_QUERY_KEY }),
      ])
    },
  })
}

/**
 * Hook to unmap an OMS source from its store.
 */
export function useUnmapOmsSourceMutation(): UseMutationResult<
  UnmapOmsSourceResponse,
  Error,
  number
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => unmapOmsSource(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: OMS_SOURCES_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: STORES_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: BASELINKER_SOURCES_QUERY_KEY }),
      ])
    },
  })
}

/**
 * Hook to sync OMS sources from an external OMS.
 */
export function useSyncOmsSourcesMutation(): UseMutationResult<
  SyncOmsSourcesResponse,
  Error,
  { omsType: OmsType; force?: boolean }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ omsType, force }) => syncOmsSources(omsType, force),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: OMS_SOURCES_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: BASELINKER_SOURCES_QUERY_KEY }),
      ])
    },
  })
}
